import {Component, HostListener, inject, OnInit, signal} from "@angular/core";
import {ActivationEnd, NavigationEnd, Router} from "@angular/router";
import {environment} from "../environments/environment";
import {OverlayContainer} from "@angular/cdk/overlay";
import {DomSanitizer} from "@angular/platform-browser";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {MatIconRegistry} from "@angular/material/icon";
import {TranslateService} from "@ngx-translate/core";
import {EventEmitterService, GlobalEventEmitterService} from "@event-emitter";
import {
	NotificationService,
	SimConfigurationService,
	SimDeviceMappingService,
	SimEnergyScheduleService,
	SimMaterialDialogService,
	SimUserExtendedService,
	SimWindowService,
	ThemeService,
} from "@services";
import {CameraLayoutMap, Modules} from "@models";
import {Translations} from "@translations";

import localeEn from "@angular/common/locales/en";
import localeFr from "@angular/common/locales/fr";
import localeDe from "@angular/common/locales/de";
import localeRu from "@angular/common/locales/ru";
import localePl from "@angular/common/locales/pl";
import localeEs from "@angular/common/locales/es";
import localeHe from "@angular/common/locales/he";
import {registerLocaleData} from "@angular/common";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {RouteData} from "./app-routing.models";
import {Theme} from "@common/types/Theme";
import Enumerable from "linq";
import {toSignal} from "@angular/core/rxjs-interop";

@UntilDestroy()
@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	animations: [
		trigger("slideInOut", [
			transition(":enter", [style({transform: "translateY(-100%)"}), animate("175ms ease-in", style({transform: "translateY(0%)"}))]),
			transition(":leave", [animate("175ms ease-in", style({transform: "translateY(-100%)"}))]),
		]),
		trigger("hamburguerX", [
			/*
              state hamburguer => is the regular 3 lines style.
              states topX, hide, and bottomX => used to style the X element
            */
			state("hamburguer", style({})),
			// style top bar to create the X
			state(
				"topX",
				style({
					transform: "rotate(45deg)",
					transformOrigin: "left",
					margin: "6px",
				}),
			),
			// hides element when create the X (used in the middle bar)
			state(
				"hide",
				style({
					opacity: 0,
				}),
			),
			// style bottom bar to create the X
			state(
				"bottomX",
				style({
					transform: "rotate(-45deg)",
					transformOrigin: "left",
					margin: "6px",
				}),
			),
			transition("* => *", [
				animate("0.3s"), // controls animation speed
			]),
		]),
	],
	providers: [EventEmitterService],
	standalone: false,
})
export class AppComponent implements OnInit {
	// Constants
	protected readonly T = Translations;
	protected readonly hasLicenseModule = SimConfigurationService.hasLicenseModule;
	protected readonly Modules = Modules;

	// Properties
	protected readonly clientProperties = SimDeviceMappingService.clientProperties;
	public readonly showNav = signal<boolean>(false);
	public readonly showBurgerMenu = signal<boolean>(true);
	public readonly showSystemWarning = signal<boolean>(false);
	public readonly themeClass = signal<string | null>(null);
	public readonly showCrossFade = signal<boolean>(false);
	public nextSystemShutdownInSeconds = 0;
	public previousUrl: string | null = null;
	public currentUrl: string | null = null;
	#nextSystemShutdownTriggerKey: string | null = null;
	#showTranslationKeys = false;
	#showPermissionTooltips = false;
	#afterFirstActivationEnd = false;

	// Injections
	public userService = inject(SimUserExtendedService);
	public themeService = inject(ThemeService);
	readonly translate = inject(TranslateService);
	readonly router = inject(Router);
	readonly eventEmitter = inject(EventEmitterService);
	readonly notificationService = inject(NotificationService);
	readonly overlayContainer = inject(OverlayContainer);
	readonly matIconRegistry = inject(MatIconRegistry);
	readonly domSanitizer = inject(DomSanitizer);
	readonly #windowService = inject(SimWindowService);
	readonly #matDialog = inject(SimMaterialDialogService);
	readonly #energyScheduleService = inject(SimEnergyScheduleService);
	readonly #globalEventEmitterService = inject(GlobalEventEmitterService);
	readonly #configurationService = inject(SimConfigurationService);

	// Derived Properties
	public readonly isTwoScreen = toSignal(this.#windowService.isTwoScreen);

	ngOnInit(): void {
		this.subscribeToObservables();
		this.registerMatIcons();

		registerLocaleData(localeEn, "en");
		registerLocaleData(localeDe, "de");
		registerLocaleData(localeFr, "fr");
		registerLocaleData(localeRu, "ru");
		registerLocaleData(localePl, "pl");
		registerLocaleData(localeEs, "es");
		registerLocaleData(localeHe, "he");
		registerLocaleData(localeEn, "SHOWKEYS");
	}

	/**
	 * Used to check if translation combination is clicked
	 * @param e keyevent
	 */
	@HostListener("document:keydown", ["$event"])
	onKeyDownHostListener(e: KeyboardEvent) {
		if (e.altKey && e.ctrlKey && e.code == "KeyT" && SimConfigurationService.settings.enableDebugMode) {
			this.eventEmitter.onToggleShowTranslationKeys.next({
				visible: !this.#showTranslationKeys,
			});
		} else if (e.altKey && e.ctrlKey && e.code == "KeyP" && SimConfigurationService.settings.enableDebugMode) {
			this.eventEmitter.onToggleShowPermissionTooltips.next({
				visible: !this.#showPermissionTooltips,
			});
		} else if (e.altKey && e.ctrlKey && e.code == "KeyE") {
			this.translate.use("en");
		} else if (e.altKey && e.ctrlKey && e.code == "KeyL") {
			this.router.navigate(["login"], {queryParams: {logout: true}});
		}
	}

	private handlePermissionTooltipToggle(isVisible: boolean) {
		this.#showPermissionTooltips = isVisible;
		this.notificationService.notifySuccess(`Permission tooltips: ${this.#showPermissionTooltips ? "ON" : "OFF"}`);

		// This abhorrent hack disables non-permission tooltips in the entire body
		this.#showPermissionTooltips ? document.body.classList.add("disableTooltips") : document.body.classList.remove("disableTooltips");
	}

	public async openUserCard() {
		if (this.hasLicenseModule(Modules.UserManagment)) {
			await this.#matDialog.openUserCardMatDialog({
				userData: this.userService.user,
			});
		}
	}

	public async cancelSystemShutdown() {
		if (!this.#nextSystemShutdownTriggerKey) {
			return;
		}

		const response = await this.#energyScheduleService.skipTrigger({
			triggerKey: this.#nextSystemShutdownTriggerKey,
		});

		if (response) {
			this.showSystemWarning.set(false);
			this.#nextSystemShutdownTriggerKey = null;
			this.nextSystemShutdownInSeconds = 0;
		}
	}

	private registerMatIcons() {
		let svgIconPath = environment.baseHref + "/assets/icons/duotone/*.svg";
		let svgIcons = [
			"comment-dots-duotone",
			"thumbs-up-duotone",
			"exclamation-triangle-duotone",
			"maskventilation-duotone",
			"laryngoscope-duotone",
			"heartRestart_hand_duotone",
			"heartRestart_defi_duotone",
			"monitor-heart-rate-duotone",
			"venflon-duetone",
			"syringe-duotone",
			"stethoscope-duotone",
			"thermometer-duotone",
			"pills-duotone",
			"explosion-duotone",
			"weapon-duotone",
			"IO_drilling-duotone",
		];

		for (const icon of svgIcons) {
			this.matIconRegistry.addSvgIcon(icon, this.domSanitizer.bypassSecurityTrustResourceUrl(svgIconPath.replace("*", icon)));
		}

		svgIconPath = environment.baseHref + "/assets/icons/audioIcons/*.svg";
		svgIcons = [
			"audio_rec",
			"audio_rec_not",
			"audio_solo",
			"audio_speaker",
			"audio_speaker_not",
			"audio_transmission",
			"audio_transmission_not",
			"VOP",
			"xlr",
			"xlr_2",
		];

		for (const icon of svgIcons) {
			this.matIconRegistry.addSvgIcon(icon, this.domSanitizer.bypassSecurityTrustResourceUrl(svgIconPath.replace("*", icon)));
		}

		svgIconPath = environment.baseHref + "/assets/icons/appIcons/*.svg";
		svgIcons = [
			"app_archive",
			"app_Assessments",
			"app_CamView",
			"app_debriefing",
			"app_EventPlanner",
			"app_goLive",
			"app_kommunTraining",
			"app_OSCE",
			"app_OSCEDesigner",
			"app_Presentation",
			"app_RoomManagement",
			"app_scenrioDesigner",
			"app_StageDesigner",
			"app_training",
			"app_userManagement",
			"app_Checklists",
			"app_ScenarioMedia",
			"app_HealthMonitor",
			"app_Bookmarks",
			"app_AudioToRoom",
		];

		for (const icon of svgIcons) {
			this.matIconRegistry.addSvgIcon(icon, this.domSanitizer.bypassSecurityTrustResourceUrl(svgIconPath.replace("*", icon)));
		}

		svgIconPath = environment.baseHref + "/assets/icons/screenPatterns/*.svg";

		svgIcons = Enumerable.from(CameraLayoutMap.entries())
			.selectMany((x) => Array.from(x[1].svgs.values()))
			.toArray();

		for (const icon of svgIcons) {
			this.matIconRegistry.addSvgIcon(icon, this.domSanitizer.bypassSecurityTrustResourceUrl(svgIconPath.replace("*", icon)));
		}
	}

	private subscribeToObservables() {
		const routesExcludedFromDock = [
			"scenario-archive",
			"event-manager",
			"scenario-editor",
			"user-manager",
			"media-management",
			"digital-signage",
			"web-manager/list/energy-saving/energy-schedules/edit",
			"web-manager/list/assessments/user-recording-assessments",
		];
		const routesExcludedFromCrossFade = [
			"sim-debriefing-tablet",
			"simrecorder",
			"stage-designer",
			"login",
			"auto-login",
			"sim-player",
			"scenario-archive/player",
			"master-of-ceremony-app/multi-room-recording",
			"participant-device/examiner",
			"participation-view",
		];
		this.router.events.subscribe((e) => {
			if (e instanceof NavigationEnd) {
				//reset navigation
				this.#afterFirstActivationEnd = false;
				this.previousUrl = this.currentUrl;
				this.currentUrl = e.url;

				if (this.isTwoScreen()) {
					this.showCrossFade.set(!routesExcludedFromCrossFade.some((r) => e.urlAfterRedirects.includes(r)));
				}

				if (routesExcludedFromDock.some((r) => e.urlAfterRedirects.includes(r))) {
					this.#globalEventEmitterService.closeSideNavigation.next({
						visible: false,
					});
				}

				if (
					!(
						e.urlAfterRedirects.startsWith("/" + environment.routes.login) ||
						e.urlAfterRedirects.startsWith("/" + environment.routes.autoLogin) ||
						e.urlAfterRedirects.startsWith("/" + environment.routes.simPlayer)
					)
				) {
					this.#globalEventEmitterService.onShowNavigation.next({visible: true});
					this.#globalEventEmitterService.onShowBurgerMenu.next({visible: true});
				}
			} else if (e instanceof ActivationEnd) {
				if (!this.#afterFirstActivationEnd && e.snapshot.component) {
					this.#afterFirstActivationEnd = true;

					//handle route data
					const data = <RouteData>e.snapshot.data;
					if (data?.theme == Theme.light) {
						this.themeService.setTheme(ThemeService.lightTheme);
					} else {
						this.themeService.setTheme(ThemeService.darkTheme);
					}
				}
			}
		});

		this.eventEmitter.onToggleShowTranslationKeys.subscribe((value) => {
			this.#showTranslationKeys = value.visible;
		});

		this.eventEmitter.onToggleShowPermissionTooltips.subscribe((value) => {
			this.handlePermissionTooltipToggle(value.visible);
		});

		this.eventEmitter.onSettingsChanged.subscribe(async () => {
			await this.#configurationService.reloadConfig();
		});

		this.eventEmitter.onSystemShutdownImminent.subscribe(async ({triggerKey}) => {
			const nextShutdownDateStr = await this.#energyScheduleService.getStopTriggerTimeAsync(triggerKey);
			const remainingTime = new Date(nextShutdownDateStr).getTime() - Date.now();

			this.nextSystemShutdownInSeconds = Math.floor(remainingTime / 1000);
			this.showSystemWarning.set(this.nextSystemShutdownInSeconds > 0);
			this.#nextSystemShutdownTriggerKey = triggerKey;
		});

		this.themeService.themeSubject.pipe(untilDestroyed(this)).subscribe((theme: string) => {
			if (this.themeClass() == theme) {
				return;
			}
			const previousTheme = this.themeClass();
			if (previousTheme) {
				document.body.classList.remove(previousTheme);
			}
			this.themeClass.set(theme);
			document.body.classList.add(theme);

			const overlayContainerClasses = this.overlayContainer.getContainerElement().classList;
			const themeClassesToRemove = Array.from(overlayContainerClasses).filter((item: string) => item.includes("-theme"));
			if (themeClassesToRemove.length) {
				overlayContainerClasses.remove(...themeClassesToRemove);
			}
			overlayContainerClasses.add(theme);
		});

		this.eventEmitter.onShowNavigation.subscribe((args) => this.showNav.set(args.visible));
		this.eventEmitter.onShowBurgerMenu.subscribe((args) => this.showBurgerMenu.set(args.visible));
	}
}
